import React, { useState } from 'react';
import { MarketingPanel } from '../../shared/components';
import { toFixed2 } from '../../shared/utils';

/*
 * API shape note:
 * The backend returns inconsistent casing across objects:
 *   sender.*       — camelCase  (companyName, email, phone, whatsapp, Website)
 *   receiver.*     — PascalCase (CompanyName, ContactPerson, email, phone)
 *   transactionDetails.* — PascalCase for some fields (EntryNo, EntryDate, CurrencyCode)
 *
 * We normalise here with `pick` so the rest of the component is consistent.
 */
function pick(obj, ...keys) {
  if (!obj) return {};
  // Try each key variant: exact, lowercase, capitalised first letter
  const result = {};
  for (const key of keys) {
    const variants = [key, key.toLowerCase(), key[0].toUpperCase() + key.slice(1)];
    for (const v of variants) {
      if (obj[v] !== undefined && obj[v] !== null && obj[v] !== '') {
        result[key] = obj[v];
        break;
      }
    }
  }
  return result;
}

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="detail-row">
      <span className="detail-label">{label}</span>
      <span className="detail-value">{value}</span>
    </div>
  );
}

function AddressSection({ address }) {
  if (!address) return null;
  return (
    <>
      <hr className="detail-divider" />
      <DetailRow label="Address" value={address.fullAddress} />
      <DetailRow label="City"    value={address.city} />
      <DetailRow label="Pincode" value={address.pincode} />
      <DetailRow label="Country" value={address.country} />
    </>
  );
}

const SECTIONS = [
  {
    id: 'sender',
    label: 'Sender',
    iconClass: 'accordion-trigger-icon--sender',
    icon: 'bi-building',
  },
  {
    id: 'receiver',
    label: 'Receiver',
    iconClass: 'accordion-trigger-icon--receiver',
    icon: 'bi-person',
  },
  {
    id: 'document',
    label: 'Document Details',
    iconClass: 'accordion-trigger-icon--document',
    icon: 'bi-file-earmark-text',
  },
];

export function TransactionDetails({ transaction }) {
  const { sender, receiver, transactionDetails } = transaction || {};
  const [openSection, setOpenSection] = useState('document');

  // Normalise API casing inconsistencies
  const s  = pick(sender,             'companyName', 'gstin', 'pan', 'email', 'phone', 'whatsapp', 'Website');
  const r  = pick(receiver,           'CompanyName', 'ContactPerson', 'pan', 'gstin', 'email', 'phone');
  const td = pick(transactionDetails, 'transactionType', 'EntryNo', 'EntryDate', 'CurrencyCode', 'totalTaxAmount', 'totalAmount');

  const toggle = (id) => setOpenSection((prev) => (prev === id ? null : id));

  return (
    <div className="sidebar-panel">
      {/*
        Accordion uses role="list" + role="listitem" so screen readers announce
        "3 items" rather than treating it as a navigation landmark.
        Each trigger is a <button> with aria-expanded + aria-controls.
        Section headings are <h2> to give the page a proper document outline.
      */}
      <div className="details-accordion" role="list">
        {SECTIONS.map(({ id, label, iconClass, icon }) => {
          const isOpen    = openSection === id;
          const triggerId = `accordion-trigger-${id}`;
          const panelId   = `accordion-panel-${id}`;

          return (
            <div className="accordion-section" key={id} role="listitem">
              <h2 className="accordion-heading">
                <button
                  id={triggerId}
                  className={`accordion-trigger${isOpen ? ' is-open' : ''}`}
                  onClick={() => toggle(id)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <span className="accordion-trigger-label">
                    <span className={`accordion-trigger-icon ${iconClass}`} aria-hidden="true">
                      <i className={`bi ${icon}`}></i>
                    </span>
                    {label}
                  </span>
                  <i className="bi bi-chevron-down accordion-chevron" aria-hidden="true"></i>
                </button>
              </h2>

              <div
                id={panelId}
                role="region"
                aria-labelledby={triggerId}
                className={`accordion-content${isOpen ? ' is-open' : ''}`}
                hidden={!isOpen}
              >
                <div className="accordion-body">
                  {id === 'sender' && (
                    <>
                      <DetailRow label="Company"  value={s.companyName} />
                      <DetailRow label="GSTIN"    value={s.gstin} />
                      <DetailRow label="PAN"      value={s.pan} />
                      <DetailRow label="Email"    value={s.email} />
                      <DetailRow label="Phone"    value={s.phone} />
                      <DetailRow label="WhatsApp" value={s.whatsapp} />
                      <DetailRow label="Website"  value={s.Website} />
                      <AddressSection address={sender?.address} />
                    </>
                  )}
                  {id === 'receiver' && (
                    <>
                      <DetailRow label="Customer" value={r.CompanyName} />
                      <DetailRow label="Contact"  value={r.ContactPerson} />
                      <DetailRow label="PAN"      value={r.pan} />
                      <DetailRow label="GSTIN"    value={r.gstin} />
                      <DetailRow label="Email"    value={r.email} />
                      <DetailRow label="Phone"    value={r.phone} />
                      <AddressSection address={receiver?.address} />
                    </>
                  )}
                  {id === 'document' && (
                    <>
                      <DetailRow label="Type"     value={td.transactionType} />
                      <DetailRow label="Entry No" value={td.EntryNo} />
                      <DetailRow label="Date"     value={td.EntryDate} />
                      <DetailRow label="Currency" value={td.CurrencyCode} />
                      <DetailRow label="Tax"      value={toFixed2(td.totalTaxAmount)} />
                      <DetailRow label="Net Amt"  value={toFixed2(td.totalAmount)} />
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <MarketingPanel />
    </div>
  );
}
